import { motion } from 'framer-motion';
import React from 'react';

import { CroissantCsatSlider } from '@/shared/CroissantCsatSlider';

export interface CheckoutSuccessPanelProps {
  isGeneratingPDF: boolean;
  onNovoPedido: () => void;
  onGerarPdf: () => void;
  csatRating: number;
  onCsatRatingChange: (n: number) => void;
  csatFeedback: string;
  onCsatFeedbackChange: (s: string) => void;
  csatSubmitted: boolean;
  onSubmitCsat: () => void;
  confirmationEmailFailed?: boolean;
}

/** Conteúdo do modal de orçamento finalizado + CSAT. */
export const CheckoutSuccessPanel: React.FC<CheckoutSuccessPanelProps> = ({
  isGeneratingPDF,
  onNovoPedido,
  onGerarPdf,
  csatRating,
  onCsatRatingChange,
  csatFeedback,
  onCsatFeedbackChange,
  csatSubmitted,
  onSubmitCsat,
  confirmationEmailFailed = false,
}) => {
  return (
    <>
      <motion.div
        style={{
          marginBottom: '20px',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <motion.div
          style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            backgroundColor: '#3e400f',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{
            duration: 0.5,
            delay: 0.3,
            type: 'spring',
            stiffness: 200,
            damping: 15,
          }}
        >
          <motion.svg
            width='30'
            height='30'
            viewBox='0 0 24 24'
            fill='none'
            stroke='white'
            strokeWidth='3'
            strokeLinecap='round'
            strokeLinejoin='round'
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{
              duration: 0.5,
              delay: 0.5,
              ease: 'easeInOut',
            }}
          >
            <motion.path d='M20 6L9 17l-5-5' />
          </motion.svg>
        </motion.div>
      </motion.div>

      <h2
        style={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: '#3e400f',
          marginBottom: '16px',
        }}
      >
        Orçamento Finalizado!
      </h2>

      <p
        style={{
          fontSize: '16px',
          color: '#666',
          marginBottom: '32px',
          lineHeight: '1.5',
        }}
      >
        {confirmationEmailFailed
          ? 'Seu orçamento foi registrado com sucesso. O e-mail de confirmação não pôde ser enviado; você pode gerar o PDF.'
          : 'Em breve nossa equipe entrará em contato para confirmar os detalhes.'}
      </p>

      <div
        style={{
          width: '100%',
          marginTop: '8px',
          marginBottom: '28px',
          paddingTop: '24px',
          borderTop: '1px solid #ececec',
          textAlign: 'left',
        }}
      >
        <h3
          style={{
            fontSize: '16px',
            fontWeight: 600,
            color: '#3e400f',
            margin: '0 0 20px 0',
            textAlign: 'center',
            lineHeight: 1.4,
          }}
        >
          Como foi a sua experiência com esse orçamento?
        </h3>
        {!csatSubmitted ? (
          <>
            <CroissantCsatSlider
              rating={csatRating}
              onChange={onCsatRatingChange}
            />
            <label
              htmlFor='csat-feedback-checkout'
              style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: 500,
                color: '#374151',
                marginTop: '24px',
                marginBottom: '8px',
                lineHeight: 1.45,
              }}
            >
              Conte-nos como foi a sua experiência com o nosso portal de
              orçamentos (Opcional)
            </label>
            <textarea
              id='csat-feedback-checkout'
              value={csatFeedback}
              onChange={e => onCsatFeedbackChange(e.target.value)}
              placeholder='Conte como foi montar este orçamento, o que achou do atendimento…'
              rows={3}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical',
                minHeight: '80px',
              }}
            />
            <div style={{ textAlign: 'center', marginTop: '16px' }}>
              <motion.button
                type='button'
                onClick={onSubmitCsat}
                style={{
                  background: '#fff',
                  color: '#3e400f',
                  border: '2px solid #3e400f',
                  borderRadius: '8px',
                  padding: '10px 22px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Enviar avaliação
              </motion.button>
            </div>
          </>
        ) : (
          <p
            style={{
              textAlign: 'center',
              fontSize: '15px',
              color: '#3e400f',
              margin: '8px 0 0 0',
              fontWeight: 500,
            }}
          >
            Obrigado — sua avaliação foi registrada.
          </p>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'center',
          flexWrap: 'nowrap',
          width: '100%',
        }}
      >
        <motion.button
          onClick={onNovoPedido}
          style={{
            background: '#3e400f',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            padding: '12px 24px',
            fontSize: '14px',
            fontWeight: 'bold',
            cursor: 'pointer',
            flex: '1 1 0',
            minWidth: 0,
          }}
          whileHover={{
            scale: 1.05,
            y: -2,
            boxShadow: '0 8px 20px rgba(62, 64, 15, 0.3)',
          }}
          whileTap={{ scale: 0.95 }}
          transition={{
            type: 'spring',
            stiffness: 400,
            damping: 17,
          }}
        >
          Fazer Novo Orçamento
        </motion.button>
        <motion.button
          onClick={() => onGerarPdf()}
          disabled={isGeneratingPDF}
          style={{
            background: isGeneratingPDF ? '#9ca3af' : '#3e400f',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            padding: '12px 24px',
            fontSize: '14px',
            fontWeight: 'bold',
            cursor: isGeneratingPDF ? 'not-allowed' : 'pointer',
            opacity: isGeneratingPDF ? 0.7 : 1,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            justifyContent: 'center',
            minWidth: 0,
            flex: '1 1 0',
          }}
          whileHover={
            !isGeneratingPDF
              ? {
                  scale: 1.05,
                  y: -2,
                  boxShadow: '0 8px 20px rgba(62, 64, 15, 0.3)',
                }
              : {}
          }
          whileTap={!isGeneratingPDF ? { scale: 0.95 } : {}}
          transition={{
            type: 'spring',
            stiffness: 400,
            damping: 17,
          }}
        >
          {isGeneratingPDF ? (
            <>
              <svg
                width='16'
                height='16'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
                className='pdf-spinner'
              >
                <line x1='12' y1='2' x2='12' y2='6'></line>
                <line x1='12' y1='18' x2='12' y2='22'></line>
                <line x1='4.93' y1='4.93' x2='7.76' y2='7.76'></line>
                <line x1='16.24' y1='16.24' x2='19.07' y2='19.07'></line>
                <line x1='2' y1='12' x2='6' y2='12'></line>
                <line x1='18' y1='12' x2='22' y2='12'></line>
                <line x1='4.93' y1='19.07' x2='7.76' y2='16.24'></line>
                <line x1='16.24' y1='7.76' x2='19.07' y2='4.93'></line>
              </svg>
              Gerando PDF...
            </>
          ) : (
            'Gerar PDF'
          )}
        </motion.button>
      </div>
    </>
  );
};
